import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import type { BackendClient } from '../bridge/backend-client';
import type { DashboardActivityEntry } from '../../core/bridge/connect-client';
import { type LogLine } from '../components/log-tree';
import '../components/log-tree';

/*
 * <vitesse-dashboard-activity> — the board's change trail, rendered as LOGS.
 *
 * It had its own list markup: a hand-rolled <ol> with its own row spacing, its
 * own timestamps, its own empty state — a second way of drawing a list of
 * dated lines in an app that already has the one log view every other entity
 * uses. The controller page, the model page and the integrations page all
 * point <vitesse-log-tree> at their stream; this page looked like none of
 * them, and the difference taught nothing. Now it is the same component with
 * the same chrome — search, columns, virtualization — for free.
 *
 * THE ADAPTER, because the trail is FETCHED and the log view consumes a
 * STREAM. A change trail is read when somebody wonders, which is rarely; a
 * standing subscription would hold a stream open on every board to deliver a
 * message a few times a week. So `connect` builds a MessageChannel, replays
 * the fetched entries as the delta frames a live stream would have produced,
 * and hands over the port. The log view cannot tell the difference, which is
 * the point: one consumer shape, either producer.
 */
@customElement('vitesse-dashboard-activity')
export class DashboardActivity extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property({ type: String }) gatewayId = '';
  @property({ type: String }) dashboardId = '';
  /** Bumped by the page when a save lands, so the trail refreshes without this
   *  view polling for a change it can be told about. */
  @property({ type: Number }) revision = 0;
  /** The signed-in user's id + display name — resolves the trail's OLDER
   *  entries, written before the gateway recorded names. Only one's own id is
   *  resolvable client-side (colleagues' names are not in this browser), which
   *  is also the honest scope: mapping an id we can verify, never guessing. */
  @property({ type: String }) selfId = '';
  @property({ type: String }) selfName = '';
  protected createRenderRoot(): HTMLElement {
    return this; // light DOM — the log view's global SCSS must reach it
  }

  private toLine = (item: unknown): LogLine => {
    const e = item as DashboardActivityEntry;
    /*
     * The actor verbatim — since the gateway records display names, the entry
     * already says "Max Leroy". No "you": a wall display is read by whoever
     * stands in front of it, and second person addressed at the wrong person
     * is worse than a name. Older entries hold the raw user id the trail
     * recorded before names existed; they render as they are, because
     * rewriting a record to look nicer than it was is how records stop being
     * believed.
     */
    const raw = e.actor || t('dashboards.someone');
    // New entries carry the display name the gateway recorded; old ones carry
    // the raw user id. The one id this browser can vouch for is its own.
    const who = this.selfName && raw === this.selfId ? this.selfName : raw;
    const created = e.action === 'created';
    return {
      tsMs: e.atMs,
      severity: 'info',
      // The KIND is the chip, so the message carries only who and what — the
      // status column stops being empty and the message stops repeating it.
      status: created
        ? { label: t('dashboards.statusCreated'), color: 'blue' }
        : { label: t('dashboards.statusEdited'), color: 'gray' },
      title: created ? `${who} — ${e.detail}` : `${who} — ${e.detail || t('dashboards.actionEdited')}`,
    };
  };

  // STABLE connect (see model-logs-section): a fresh arrow per render would
  // re-render the whole log tree every parent render.
  private readonly connectTrail = async (): Promise<MessagePort> => {
    const { port1, port2 } = new MessageChannel();
    const entries = this.backend ? await this.backend.api.dashboardActivity(this.gatewayId, this.dashboardId) : [];
    for (const e of entries) port1.postMessage({ kind: 'delta', op: 'upsert', item: e });
    // The channel stays open but silent — exactly what a quiet live stream
    // looks like. The log view's teardown closes it with the subscription.
    return port2;
  };

  render() {
    if (!this.backend) return nothing;
    return html`<vitesse-log-tree
      .subscriptionKey=${`${this.gatewayId}/${this.dashboardId}@${this.revision}`}
      .connect=${this.connectTrail}
      .toLine=${this.toLine}
      .emptyTitle=${t('dashboards.noActivity')}
      .emptyDesc=${''}
    ></vitesse-log-tree>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-dashboard-activity': DashboardActivity;
  }
}
